// Marketing area: automated segment campaigns + journeys (universal automation engine).
export const marketing = {
  automations: {
    title: "Automated campaigns",
    subtitle: "Automatically sent emails based on segments",
    newCampaign: "New campaign",
    howItWorksTitle: "How does it work?",
    howItWorksText:
      "Create a campaign linked to a segment. Every hour, new customers in the segment are checked and automatically sent an email. You can enable AI personalization of content for each customer.",
    searchPlaceholder: "Search campaigns…",
    empty: "No automated campaigns",
    createFirst: "Create first campaign",
    sentBadge: "{{n}} sent",
    openedBadge: "{{n}} opened",
    clickedBadge: "{{n}} clicked",
    lastSent: "Last sent: {{date}}",
    triggerNow: "Trigger now",
    activate: "Activate",
    deactivate: "Deactivate",
    details: "Details",
    statusChanged: "Campaign status changed",
    statusChangeFailed: "Failed to change campaign status",
    deleted: "Campaign deleted",
    deleteFailed: "Failed to delete campaign",
    triggered_one: "Sent {{count}} email",
    triggered_few: "Sent {{count}} emails",
    triggered_other: "Sent {{count}} emails",
    triggerFailed: "Failed to trigger campaign",
    deleteConfirmTitle: "Delete campaign",
    deleteConfirmMessage: "Are you sure you want to delete this campaign? This cannot be undone.",
    triggerConfirmTitle: "Trigger campaign now",
    triggerConfirmMessage:
      "Trigger this campaign now? Emails will be sent to new customers in the segment.",
    triggerConfirmButton: "Trigger",
    createTitle: "New automated campaign",
    nameLabel: "Campaign name *",
    namePlaceholder: "e.g. Welcome email",
    segmentLabel: "Segment *",
    selectSegment: "Select segment",
    segmentCustomers_one: "{{count}} customer",
    segmentCustomers_few: "{{count}} customers",
    segmentCustomers_other: "{{count}} customers",
    subjectLabel: "Subject *",
    subjectPlaceholder: "Email subject",
    contentLabel: "Email content *",
    contentPlaceholder: "HTML email content…",
    variablesHint: "Variables:",
    sendTimeLabel: "Send time (UTC)",
    aiPersonalize: "AI personalization",
    aiPersonalizeHint: "AI will customize email content for each customer individually",
    aiPromptLabel: "AI prompt",
    aiPromptPlaceholder: "Instructions for AI on how to personalize the email…",
    fillRequired: "Please fill in all required fields",
    created: "Campaign created",
    createFailed: "Failed to create campaign",
    statSent: "Sent",
    statOpened: "Opened",
    statClicked: "Clicked",
    statSegment: "Segment",
    statCustomers: "customers",
    testSendTitle: "Test send",
    sendTest: "Send test",
    testSent: "Test email would be sent to {{email}}",
    testFailed: "Failed to send test email",
    enterTestEmail: "Enter test email",
    recentSends: "Recent sends",
    sendStatus: {
      sent: "sent",
      failed: "failed",
      pending: "pending",
    },
    contentTitle: "Email content",
    notFound: "Campaign not found",
  },
  journeysList: {
    title: "Journeys",
    subtitle: "Universal automations — multi-step, event & schedule driven, across any subject.",
    seedCatalog: "Seed catalog",
    seedCatalogTitle: "Idempotently create the 8 starter journeys (disabled)",
    newJourney: "New journey",
    empty: "No journeys yet.",
    emptyHint:
      "Create one, or seed the starter catalog from the API (POST /api/admin/journeys/seed).",
    colSubject: "Subject",
    colTrigger: "Trigger",
    colSend: "Send",
    colStats: "Stats",
    colEnabled: "Enabled",
    systemChip: "system",
    stepCount_one: "{{count}} step",
    stepCount_few: "{{count}} steps",
    stepCount_other: "{{count}} steps",
    enrolled: "Enrolled",
    completed: "Completed",
    toggleEnabled: "Toggle enabled",
    toggleFailed: "Failed to toggle journey",
    deleted: "Journey deleted",
    deleteFailed: "Failed to delete journey",
    seedApplied: "Seed catalog applied ({{created}} new, {{updated}} updated)",
    seedFailed: "Failed to seed catalog (admin only)",
    deleteConfirm: "Delete journey “{{name}}”? This removes its enrollments.",
    summaryTimeOffset: "{{label}}: {{offset}}d from {{field}}",
    summaryInactivity: "{{label}}: {{days}}d on {{field}}",
    summaryRecurring: "{{label}}: every {{days}}d",
  },
  journeyEdit: {
    newTitle: "New journey",
    editTitle: "Edit journey",
    saved: "Journey saved",
    nameRequired: "Name is required",
    stepRequired: "Add at least one step",
    actionRequired: "Every step needs an action",
    subjectType: "Subject type",
    enabled: "Enabled",
    triggerTitle: "Trigger",
    entryConditionsTitle: "Entry conditions (optional)",
    stepsTitle: "Steps",
    addStep: "Add step",
    policyTitle: "Policy",
    sendClass: "Send class",
    sendClassHint:
      "Marketing sends pass the suppression + frequency-cap gate; transactional/ops don't.",
    reenrollment: "Re-enrollment",
    maxConcurrent: "Max concurrent / subject",
  },
  journeyEnrollments: {
    title: "Enrollments",
    empty: "No enrollments yet.",
    noRuns: "No step runs yet.",
    meta: "step {{step}} · entered {{date}}",
    metaNext: "· next {{date}}",
  },
  journeyTrigger: {
    type: "Trigger type",
    statusTo: "When status becomes (to)",
    statusFrom: "Only from (optional)",
    field: "Field",
    equalsValue: "Equals value",
    equalsPlaceholder: "(or use ≥ / ≤)",
    dateField: "Date field",
    daysOffset: "Days offset (− before, + after)",
    atTime: "At time (HH:MM, optional)",
    eventType: "Event type",
    sinceField: "Inactive since field",
    daysIdle: "Days idle",
    onlyIfStatus: "Only if status (optional)",
    everyNDays: "Every N days",
    manualHint: "Enrolled only manually (no automatic trigger).",
  },
  journeyConditions: {
    match: "Match",
    matchAll: "all (AND)",
    matchAny: "any (OR)",
    of: "of:",
    valuePlaceholder: "value",
    addCondition: "Add condition",
  },
  journeyStep: {
    stepTitle: "Step {{n}}",
    waitBefore: "Wait before running",
    days: "days",
    hours: "hours",
    atTime: "at time (optional)",
    runOnlyIf: "Run only if (optional)",
    ifConditionsFail: "If conditions fail:",
    skipStep: "skip this step",
    exitJourney: "exit the journey",
    addAction: "Add action",
  },
  journeyAction: {
    toCustomer: "to customer",
    toAssignee: "to assignee",
    toAdmin: "to admin",
    toField: "to a field…",
    varsKeyPlaceholder: "vars key (e.g. email)",
    inlineSubjectBody: "— inline subject/body —",
    templateOption: "template: {{name}}",
    subjectPlaceholder: "Subject (supports {{vars}})",
    bodyPlaceholder: "HTML body (supports {{vars}})",
    smsChannelHint: "Channel not yet configured — sends are recorded as skipped.",
    messagePlaceholder: "Message (supports {{vars}})",
    taskTitlePlaceholder: "Task title (supports {{vars}})",
    dueInDays: "due in days",
    kindTask: "task",
    kindCallback: "callback",
    kindSupplierFollowup: "supplier follow-up",
    unassigned: "unassigned",
    subjectOwner: "subject's owner",
    specificUserEllipsis: "specific user…",
    roundRobin: "round robin",
    specificUser: "specific user",
    notificationPlaceholder: "Notification message (supports {{vars}})",
    pickSegment: "— pick segment —",
    docContract: "contract",
    docItinerary: "itinerary",
    docVoucher: "voucher",
    docPackage: "travel package",
    webhookBodyPlaceholder: "Body template (supports {{vars}}); blank → default JSON",
    agentInstructionPlaceholder:
      "Instruction for the agent (supports {{vars}}), e.g. “This customer last travelled {{lastTripDate}}. Draft a personalised win-back offer.”",
    allowMoney: "Allow money actions (invoices, refunds, date changes…)",
    agentHint:
      "The agent only <strong>proposes</strong> — a person approves it in the agent queue. Nothing is sent or charged automatically.",
  },
  journeyEnums: {
    subjectType: {
      order: "Order",
      lead: "Inquiry",
      customer: "Customer",
      service_booking: "Service booking",
      trip_date: "Departure date",
      invoice: "Invoice",
      review: "Review",
      complaint: "Complaint",
      participant: "Participant",
    },
    triggerType: {
      status_change: "Status change",
      field_change: "Field change / threshold",
      time_offset: "Days from a date",
      event: "Event",
      inactivity: "Inactivity",
      recurring: "Recurring",
      manual: "Manual only",
    },
    sendClass: {
      marketing: "Marketing (gated)",
      transactional: "Transactional",
      ops: "Operational",
    },
    reenrollment: {
      once: "Once per subject",
      once_per_completion: "Once per completion",
      always: "Every time",
    },
    actionType: {
      send_email: "Send email",
      send_sms: "Send SMS",
      send_whatsapp: "Send WhatsApp",
      create_task: "Create task",
      assign: "Assign owner",
      notify_user: "Notify user",
      add_to_segment: "Add to segment",
      remove_from_segment: "Remove from segment",
      generate_document: "Generate document",
      webhook: "Webhook",
      agent_action: "Ask the agent (raise proposal)",
    },
    conditionOp: {
      eq: "is",
      neq: "is not",
      gt: ">",
      lt: "<",
      gte: "≥",
      lte: "≤",
      contains: "contains",
      in: "is one of",
      is_set: "is set",
      is_empty: "is empty",
    },
    enrollmentStatus: {
      active: "active",
      completed: "completed",
      cancelled: "cancelled",
      failed: "failed",
    },
    stepRunStatus: {
      completed: "completed",
      skipped: "skipped",
      failed: "failed",
    },
    actionRunStatus: {
      ok: "ok",
      sent: "sent",
      completed: "completed",
      skipped: "skipped",
      failed: "failed",
    },
  },
};
